import { HttpResponse } from '@angular/common/http';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FileRestrictions, UploadEvent } from '@progress/kendo-angular-upload';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, combineLatest, distinctUntilChanged, from, map, Observable, switchMap } from 'rxjs';
import { downloadBlob } from 'src/app/shared/helpers/fileHelper';
import { resizeImageTo1920 } from 'src/app/shared/helpers/image-scaling';
import { AttachmentService } from 'src/app/shared/services/attachment.service';
import { InventoryAttachmentStore } from 'src/app/stores/component/inventory-attachment-store';

/**
 * Documents and pictures of an inventory device (same UX as the device attachments).
 * Upload / list / delete go through the inventory endpoints, download and thumbnails through the shared attachment endpoints.
 */
@UntilDestroy()
@Component({
    selector: 'app-inventory-detail-attachments',
    templateUrl: './inventory-detail-attachments.component.html',
    styleUrl: './inventory-detail-attachments.component.scss',
    standalone: false
})
export class InventoryDetailAttachmentsComponent implements OnInit, OnChanges {
  @Input() deviceId: string;

  vm$: Observable<any>;

  // Upload file type restrictions
  restrictions: FileRestrictions = {
    allowedExtensions: [".jpg", ".jpeg", ".png", ".pdf"],
  };

  public documentDeletionDialogOpened = false;
  public imageDeletionDialogOpened = false;
  public selectedItemToDelete: string | null = null;

  constructor(
    private attachmentStore: InventoryAttachmentStore,
    private attachmentService: AttachmentService,
  ) {
    this.vm$ = combineLatest([
      this.attachmentStore.documents$,
      this.attachmentStore.images$,
      this.attachmentStore.isLoading$
    ]).pipe(
      map(([
        documents,
        images,
        isUploading
      ]) => ({
        documents,
        images,
        isUploading
      }))
    );
  }

  ngOnInit() {
    this.vm$
      .pipe(
        distinctUntilChanged((prev, curr) => prev.images === curr.images),
        untilDestroyed(this)
      )
      .subscribe(vm => {
        vm.images.forEach(image => {
          if (image.data) {
            return;
          }

          from(this.attachmentService.waitForThumbnail(image.id, 3, 500))
            .pipe(
              switchMap(hasThumbnail => {
                // if thumbnail exists, download midsize, otherwise the original
                if (hasThumbnail) {
                  return this.attachmentService.downloadMidsize({ attachmentId: image.id });
                }
                return this.attachmentService.download({ attachmentId: image.id });
              }),
              catchError(() =>
                this.attachmentService.download({ attachmentId: image.id })
              )
            )
            .subscribe((res: HttpResponse<Blob>) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                image.data = reader.result as string;
              };
              reader.readAsDataURL(res.body);
            });
        });
      });

  }

  // the device is loaded asynchronously by the parent, so load the files as soon as the id is known
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['deviceId'] && this.deviceId) {
      this.attachmentStore.getAttachmentsByInventoryDevice(this.deviceId);
    }
  }

  public async uploadAttachment(event: UploadEvent): Promise<void> {
    event.preventDefault();

    const formDataArray: FormData[] = [];

    for (const file of event.files) {
      // Resize image
      const processedBlob = await resizeImageTo1920(file.rawFile);
      const processedFile = new File([processedBlob], file.name, { type: file.rawFile.type });

      const formData = new FormData();
      formData.append('file', processedFile, file.name);
      formData.append('inventoryDeviceId', this.deviceId);

      formDataArray.push(formData);
    }

    if (formDataArray.length > 0) {
      this.attachmentStore.addAttachments(formDataArray);
    }
  }

  public downloadAttachment(id: string, filename?: string) {
    const fileNameToUse = filename ?? "unbenannt";
    this.attachmentService.download({ attachmentId: id }).subscribe((response) => downloadBlob(response, fileNameToUse));
  }

  public deleteAttachment(id: string) {
    this.attachmentStore.removeAttachmentById(id);
  }

  // delete confirmation dialogs
  public documentDeletionDialogClose(status: string): void {
    this.documentDeletionDialogOpened = false;

    if (status == 'delete' && this.selectedItemToDelete) {
      this.deleteAttachment(this.selectedItemToDelete);
    }

    this.selectedItemToDelete = null;
  }

  public documentDeletionDialogOpen(id: string): void {
    this.documentDeletionDialogOpened = true;
    this.selectedItemToDelete = id;
  }

  public imageDeletionDialogClose(status: string): void {
    this.imageDeletionDialogOpened = false;

    if (status == 'delete' && this.selectedItemToDelete) {
      this.deleteAttachment(this.selectedItemToDelete);
    }

    this.selectedItemToDelete = null;
  }

  public imageDeletionDialogOpen(id: string): void {
    this.imageDeletionDialogOpened = true;
    this.selectedItemToDelete = id;
  }
}